import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import api from '../api';
import { MessageCircle, X, Send } from 'lucide-react';

const sid = () => {
  let s = sessionStorage.getItem('assistant_sid');
  if (!s) { s = 'as-' + Math.random().toString(36).slice(2, 10); sessionStorage.setItem('assistant_sid', s); }
  return s;
};
const mid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function AssistantWidget() {
  const { t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMsgs((m) => [...m, { id: mid(), role: 'user', text }]);
    setBusy(true);
    try {
      const res = await api.askAssistant(text, sid(), lang);
      setMsgs((m) => [...m, { id: mid(), role: 'ai', text: res.reply }]);
    } catch {
      setMsgs((m) => [...m, { id: mid(), role: 'ai', text: lang === 'fr' ? 'Désolé, je suis indisponible pour le moment.' : 'Sorry, I am unavailable right now.' }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="assistant-toggle" onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#4b5e34] hover:bg-[#3f5230] text-white shadow-xl flex items-center justify-center transition-colors">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-[360px] h-[440px] bg-white rounded-2xl border border-neutral-200 shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-[#4b5e34] text-white px-4 py-3">
            <p className="font-display text-lg leading-tight">{lang === 'fr' ? 'Aide au choix' : 'Meal helper'}</p>
            <p className="text-[11px] text-[#d8dcc6]">{lang === 'fr' ? 'Demandez-moi une recommandation de plat' : 'Ask me for a meal recommendation'}</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#faf9f5]">
            {msgs.length === 0 && (
              <p className="text-xs text-neutral-400 text-center mt-8">{lang === 'fr' ? 'Ex. « Un plat végétarien riche en protéines ? »' : 'E.g. "A vegetarian high-protein meal?"'}</p>
            )}
            {msgs.map((m) => (
              <div key={m.id} className={`max-w-[85%] text-sm rounded-xl px-3 py-2 ${m.role === 'user' ? 'ml-auto bg-[#4b5e34] text-white' : 'bg-white border border-neutral-200 text-neutral-700'}`}>
                {m.text}
              </div>
            ))}
            {busy && <div className="bg-white border border-neutral-200 rounded-xl px-3 py-2 text-sm text-neutral-400 w-14 text-center">…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-2 border-t border-neutral-200 flex gap-2">
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={lang === 'fr' ? 'Votre question…' : 'Your question…'}
              className="flex-1 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4b5e34]" />
            <button data-testid="assistant-send" onClick={send} disabled={busy}
              className="bg-[#4b5e34] hover:bg-[#3f5230] disabled:opacity-50 text-white px-3 rounded-lg"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
}
